import React from 'react';
import { Col, Row, Select } from 'antd';
import { I18n } from '@aws-amplify/core';

import { FIRST_PAGE, PAGE_SIZE_OPTIONS } from './constants';

import * as S from './styles';

interface PaginationFooterProps {
	page: number;
	total: number;
	pageSize: number;
	showSizeChanger?: boolean;
	onPageChange: (page: number) => void;
	onPageSizeChange?: (pageSize: number) => void;
}

function renderPaginationItem(
	_page: number,
	type: 'page' | 'prev' | 'next' | 'jump-prev' | 'jump-next',
	element: React.ReactNode
): React.ReactNode {
	if (type === 'prev') {
		return <S.PreviousArrow />;
	}

	if (type === 'next') {
		return <S.NextArrow />;
	}

	return element;
}

export function PaginationFooter({
	page,
	total,
	pageSize,
	showSizeChanger = true,
	onPageChange,
	onPageSizeChange
}: Readonly<PaginationFooterProps>): JSX.Element {
	const first = total === 0 ? 0 : (page - FIRST_PAGE) * pageSize + 1;
	const last = Math.min(page * pageSize, total);
	const totalLabel = `${I18n.get('Displaying')} ${first}-${last} ${I18n.get('of')} ${total}`;

	function handlePageChange(nextPage: number) {
		onPageChange(nextPage);
	}

	function handlePageSizeChange(nextPageSize: number) {
		onPageSizeChange?.(nextPageSize);
	}

	return (
		<S.Container align="middle" justify="space-between">
			<Col>
				<Row align="middle" gutter={[8, 0]}>
					{!!showSizeChanger && (
						<Col>
							<S.Label>{I18n.get('Items per page')}</S.Label>
						</Col>
					)}
					{!!showSizeChanger && (
						<Col>
							<S.SelectWrapper>
								<Select<number>
									value={pageSize}
									options={PAGE_SIZE_OPTIONS}
									onChange={handlePageSizeChange}
									suffixIcon={<S.SelectArrow />}
								/>
							</S.SelectWrapper>
						</Col>
					)}
					<Col>
						<S.Total>{totalLabel}</S.Total>
					</Col>
				</Row>
			</Col>
			<Col>
				<S.PaginationStyled
					total={total}
					current={page}
					pageSize={pageSize}
					showSizeChanger={false}
					onChange={handlePageChange}
					itemRender={renderPaginationItem}
				/>
			</Col>
		</S.Container>
	);
}
